import { motion } from "framer-motion";
import { 
  UserCheck, ShieldAlert, Activity, TrendingUp, TrendingDown,
  Brain, Mic2, HeartHandshake, AlertTriangle, Target, Scale,
  Users, AlertOctagon, Lightbulb, CheckCircle2, Megaphone, Map
} from "lucide-react";
import { ValueBadge } from "../UtilityComponents";

// ====================================================================================
// HELPER: GLASS CONTAINER (Unified Aesthetic)
// ====================================================================================
const GlassContainer = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col ${className}`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
            {Icon && <Icon size={16} className="text-indigo-400" />}
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest">{title}</h3>
        </div>
        {/* Content */}
        <div className="p-4 flex-grow">
            {children}
        </div>
    </div>
);

// --- HELPER: ROBUST DATA FINDER ---
const findWardData = (dataset, currentWard) => {
    if (!dataset || !Array.isArray(dataset)) return null;
    
    // 1. Try Code Match (Most Accurate)
    const code = (currentWard.ward_code || currentWard.ward_id || currentWard.id || "").toString().toUpperCase();
    
    if (code) {
        const codeMatch = dataset.find(d => 
            (d.ward_code || "").toString().toUpperCase() === code
        );
        if (codeMatch) return codeMatch;
    }

    // 2. Try Name Match (Fallback)
    if (currentWard.ward_name || currentWard.name) {
        const targetName = (currentWard.ward_name || currentWard.name).toLowerCase().trim();
        return dataset.find(d => 
            (d.ward_name || "").toLowerCase().trim() === targetName
        );
    }
    return null;
};

// --- HELPER: STRATEGY EXTRACTOR ---
const getStrategyForWard = (strategies, currentWard) => {
  if (!strategies) return null;
  const categories = ['defensive_wards', 'opportunity_wards', 'priority_wards', 'speculative_wards', 'maintenance_wards']; 
  
  for (const cat of categories) {
    const found = findWardData(strategies[cat], currentWard);
    if (found) return { ...found, category: cat };
  }
  return null;
};

// --- HELPER: UNWRAPPER ---
const resolveData = (data) => {
    if (!data) return null;
    return data.minerva_analysis || data; 
};

export const renderCandidate = ({ currentWard, candidateData }) => {
    // 1. Unwrap Data
    const rootData = resolveData(candidateData);
    
    // 2. Safety Check
    if (!rootData || !currentWard) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500 border border-slate-800 rounded-xl bg-slate-900/50 p-8 text-center">
                <UserCheck size={48} className="mb-4 opacity-50" />
                <p className="font-medium">Waiting for Candidate Intelligence...</p>
                <div className="mt-4 text-[10px] text-left bg-black/30 p-4 rounded text-slate-400 font-mono w-full max-w-md overflow-hidden">
                    <p className="font-bold text-slate-300 mb-1">Debug Info:</p>
                    <p>Current Ward: {currentWard ? currentWard.ward_name : 'Missing'}</p>
                    <p>Data Received: {candidateData ? 'Yes' : 'No'}</p>
                </div>
            </div>
        );
    }

    // 3. FILTER DATA
    const hybridDataset = rootData.hybrid_ward_analysis || 
                          rootData.theia_electorate_analysis?.hybrid_ward_analysis;
    const hybridData = findWardData(hybridDataset, currentWard);
    const riskData = findWardData(rootData.contextual_risk_analysis?.ward_specific_risks, currentWard);
    const strategyData = getStrategyForWard(rootData.ward_targeting_strategy_ovid, currentWard);

    // Metrics & Profiles
    const resonance = hybridData?.resonance_metrics || {};
    const momentum = hybridData?.momentum_analysis || {};
    const isPositiveTrend = (momentum.trend || 0) >= 0;
    const candidateProfile = rootData.candidate_profile;
    const authenticityGaps = rootData.authenticity_gap_analysis?.gaps || [];

    // --- SYNTHESIZED METRICS ---
    
    // 1. Terrain Assessment (based on Compatibility)
    const compatibilityScore = strategyData?.scoring_factors?.minerva_candidate_compatibility || 0;
    let terrainType = "Contested";
    let terrainColor = "text-amber-400";
    let terrainBg = "bg-amber-500/10 border-amber-500/30";
    
    if (compatibilityScore > 0.5) {
        terrainType = "Favorable";
        terrainColor = "text-emerald-400";
        terrainBg = "bg-emerald-500/10 border-emerald-500/30";
    } else if (compatibilityScore < 0.25) {
        terrainType = "Hostile";
        terrainColor = "text-rose-400";
        terrainBg = "bg-rose-500/10 border-rose-500/30";
    }

    // 2. Communication Pivot Extraction
    // Scans risks for specific communication advice
    const commsRisk = riskData?.risks_identified?.find(r => 
        r.risk.includes("Messenger") || 
        r.recommendation.toLowerCase().includes("communication") ||
        r.recommendation.toLowerCase().includes("style")
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            
            {/* SECTION 1: HEADLINE PERFORMANCE (KPIs) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Resonance Score */}
                <div className="bg-slate-900/40 border border-slate-700/50 p-6 rounded-xl flex items-center justify-between relative overflow-hidden group hover:border-slate-600 transition-colors">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-indigo-500/20 transition-all duration-500"></div>
                    <div>
                        <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Resonance Score</div>
                        <div className="text-4xl font-black text-white tracking-tight">
                            {hybridData ? ((hybridData.final_hybrid_score || 0) * 100).toFixed(0) : '0'}
                            <span className="text-lg text-slate-500 font-normal ml-1">/100</span>
                        </div>
                        <div className="text-xs text-indigo-300 mt-2 flex items-center bg-indigo-500/10 px-2 py-1 rounded w-fit border border-indigo-500/20">
                            <Activity size={12} className="mr-1" />
                            {hybridData?.compatibility_level || 'Calculating...'}
                        </div>
                    </div>
                    <div className="h-16 w-16 rounded-full border-4 border-slate-800 flex items-center justify-center relative bg-slate-950">
                        <svg className="absolute inset-0 transform -rotate-90" viewBox="0 0 36 36">
                            <path
                                className={hybridData?.final_hybrid_score > 0.5 ? "text-emerald-500" : "text-amber-500"}
                                strokeDasharray={`${(hybridData?.final_hybrid_score || 0) * 100}, 100`}
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                        <UserCheck size={24} className="text-white" />
                    </div>
                </div>

                {/* Win Probability */}
                <div className="bg-slate-900/40 border border-slate-700/50 p-6 rounded-xl flex items-center justify-between hover:border-slate-600 transition-colors">
                    <div>
                        <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Win Probability</div>
                        <div className={`text-4xl font-black tracking-tight ${(momentum.win_probability || 0) > 0.5 ? 'text-emerald-400' : 'text-slate-200'}`}>
                            {((momentum.win_probability || 0) * 100).toFixed(1)}%
                        </div>
                        <div className={`text-xs mt-2 flex items-center font-bold px-2 py-1 rounded w-fit border ${isPositiveTrend ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-rose-400 bg-rose-500/10 border-rose-500/20'}`}>
                            {isPositiveTrend ? <TrendingUp size={12} className="mr-1" /> : <TrendingDown size={12} className="mr-1" />}
                            {momentum.trend > 0 ? '+' : ''}{momentum.trend || 0}% Trend
                        </div>
                    </div>
                     <div className="flex flex-col items-end gap-2">
                        <ValueBadge value={momentum.reason || 'Calculated'} color={momentum.win_probability > 0.5 ? "emerald" : "slate"} />
                    </div>
                </div>

                {/* Strategic Approach */}
                <div className="bg-slate-900/40 border border-slate-700/50 p-6 rounded-xl flex flex-col justify-center relative overflow-hidden hover:border-slate-600 transition-colors">
                    <div className={`absolute inset-0 opacity-5 ${
                        strategyData?.artemis_strategic_tier?.includes('PRIORITY') ? 'bg-emerald-500' : 
                        strategyData?.artemis_strategic_tier?.includes('DEFEND') ? 'bg-amber-500' : 'bg-slate-500'
                    }`} />
                    
                    <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center relative z-10">
                         <Target size={14} className="mr-2 text-accent-400" /> Strategic Tier
                    </div>
                    <div className="text-xl font-bold text-white mb-3 relative z-10 truncate">
                        {strategyData?.artemis_strategic_tier?.replace(/_/g, ' ') || 'UNCLASSIFIED'}
                    </div>
                    <div className="w-full bg-slate-950 h-2 rounded-full overflow-hidden relative z-10 border border-slate-800">
                        <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${(strategyData?.scoring_factors?.artemis_strategic_priority || 0) * 100}%` }}
                            className="bg-accent-500 h-full"
                        />
                    </div>
                     <div className="text-[10px] text-right text-slate-500 mt-1 relative z-10 font-mono">
                        Score: {strategyData?.scoring_factors?.artemis_strategic_priority?.toFixed(3) || '0.00'}
                    </div>
                </div>
            </div>

            {/* SECTION 2: STRATEGY & MOMENTUM (The "How" & "Where") */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* STRATEGY PANEL */}
                <GlassContainer title="Execution Strategy" icon={Target}>
                    {strategyData ? (
                        <div className="space-y-6">
                            
                            {/* Tactical Approach */}
                            <div>
                                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Tactical Approach</h4>
                                <div className="p-4 bg-slate-950/30 border border-slate-800/50 rounded-lg relative overflow-hidden">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent-500"></div>
                                    <p className="text-sm text-slate-200 leading-relaxed font-medium italic pl-2">
                                        "{strategyData.approach}"
                                    </p>
                                </div>
                            </div>

                            {/* Campaign Terrain */}
                            <div className={`p-4 rounded-lg border flex items-center justify-between ${terrainBg}`}>
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-full bg-slate-900/50 ${terrainColor.replace('text-', 'bg-').replace('400', '500/20')}`}>
                                        <Map size={18} className={terrainColor} />
                                    </div>
                                    <div>
                                        <div className={`text-sm font-bold ${terrainColor} uppercase tracking-wide`}>{terrainType} Terrain</div>
                                        <div className="text-[10px] text-slate-400">Based on candidate compatibility</div>
                                    </div>
                                </div>
                                <div className={`text-2xl font-black ${terrainColor}`}>
                                    {(compatibilityScore * 100).toFixed(0)}%
                                </div>
                            </div>

                            {/* Voter Pools */}
                            <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-950/30 border border-slate-800/50 p-3 rounded-lg flex flex-col justify-between hover:border-slate-700 transition-colors">
                                        <div className="text-[10px] text-slate-500 font-bold uppercase mb-1">Targetable Voters</div>
                                        <div className="flex items-end justify-between">
                                            <div className="text-xl font-bold text-white tracking-tight">{(strategyData.bq_persuadable_voters || 0).toLocaleString()}</div>
                                            <Users size={16} className="text-accent-400 mb-1 opacity-80" />
                                        </div>
                                    </div>
                                    <div className="bg-slate-950/30 border border-slate-800/50 p-3 rounded-lg flex flex-col justify-between hover:border-slate-700 transition-colors">
                                        <div className="text-[10px] text-slate-500 font-bold uppercase mb-1">Swing Pool Size</div>
                                        <div className="flex items-end justify-between">
                                            <div className="text-xl font-bold text-white tracking-tight">{(strategyData.bq_swing_pool_size || 0).toLocaleString()}</div>
                                            <Activity size={16} className="text-blue-400 mb-1 opacity-80" />
                                        </div>
                                    </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-12 text-slate-500 italic">
                            Strategic targeting data is not available for this ward.
                        </div>
                    )}
                </GlassContainer>

                {/* MOMENTUM & PSYCHOMETRICS */}
                <div className="space-y-6">
                    {/* Momentum Panel */}
                    <GlassContainer title="Momentum & Share Projection" icon={Scale}>
                         <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-slate-950/30 p-4 rounded-lg border border-slate-800/50">
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Historical Share</div>
                                <div className="text-3xl font-bold text-slate-300 tracking-tighter">{momentum.historical_share?.toFixed(1) || '0.0'}%</div>
                            </div>
                            <div className="bg-slate-950/30 p-4 rounded-lg border border-slate-800/50 relative overflow-hidden group">
                                <div className="absolute right-0 top-0 p-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                    {isPositiveTrend ? <TrendingUp size={20} className="text-emerald-500"/> : <TrendingDown size={20} className="text-rose-500"/>}
                                </div>
                                <div className="text-[10px] text-accent-400 uppercase font-bold tracking-wider mb-1">Projected Share</div>
                                <div className={`text-3xl font-bold tracking-tighter ${isPositiveTrend ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {momentum.polling_share?.toFixed(1) || '0.0'}%
                                </div>
                            </div>
                         </div>
                         <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-800/50">
                            <p className="text-xs text-slate-400 leading-relaxed">
                                <span className="font-bold text-slate-300">Projection Logic:</span> Tracking 
                                <span className={`font-bold ${isPositiveTrend ? 'text-emerald-400' : 'text-rose-400'}`}> {Math.abs(momentum.trend || 0)}% {isPositiveTrend ? 'higher' : 'lower'} </span> 
                                than baseline. {momentum.reason}
                            </p>
                         </div>
                    </GlassContainer>

                    {/* Psychometric Fit */}
                    <GlassContainer title="Psychometric Fit" icon={Brain}>
                         <div className="space-y-5">
                            {[
                                { label: 'Moral Authenticity', value: resonance.moral_authenticity, icon: HeartHandshake, desc: 'Alignment with local moral foundations' },
                                { label: 'Messenger Credibility', value: resonance.messenger_credibility, icon: Mic2, desc: 'Trust in communication style' },
                                { label: 'Cognitive Friction', value: resonance.cognitive_friction, icon: Brain, desc: 'Ease of message processing (Lower is better)', invert: true },
                                { label: 'Resilience Match', value: resonance.resilience_match, icon: Activity, desc: 'Ability to withstand local hostility' }
                            ].map((metric) => (
                                <div key={metric.label} className="group">
                                    <div className="flex justify-between items-end mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded bg-slate-800/50 text-slate-400 group-hover:text-indigo-400 group-hover:bg-indigo-500/10 transition-colors">
                                                <metric.icon size={12} />
                                            </div>
                                            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">{metric.label}</span>
                                        </div>
                                        <span className={`text-xs font-mono font-bold ${metric.invert ? (metric.value > 0.6 ? 'text-rose-400' : 'text-emerald-400') : (metric.value > 0.6 ? 'text-emerald-400' : 'text-amber-400')}`}>
                                            {(metric.value * 100).toFixed(0)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-950 rounded-full h-2 border border-slate-800/50">
                                        <motion.div 
                                            initial={{ width: 0 }}
                                            animate={{ width: `${metric.value * 100}%` }}
                                            transition={{ duration: 1 }}
                                            className={`h-full rounded-full shadow-[0_0_10px_rgba(0,0,0,0.2)] ${metric.invert ? 'bg-gradient-to-r from-emerald-500 to-rose-500' : 'bg-gradient-to-r from-blue-600 to-blue-400'}`}
                                            style={metric.invert ? { width: `${metric.value * 100}%` } : {}}
                                        />
                                    </div>
                                </div>
                            ))}
                         </div>
                    </GlassContainer>
                </div>
            </div>

            {/* SECTION 3: THREATS & FRICTION (Bottom Section - Semantic Colors Preserved) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* WARD SPECIFIC RISKS */}
                <div className="bg-rose-950/10 border border-rose-900/30 p-5 rounded-xl h-full backdrop-blur-sm">
                    <div className="flex items-center space-x-2 mb-4 border-b border-rose-900/30 pb-3">
                        <AlertOctagon size={18} className="text-rose-500" />
                        <h3 className="text-sm font-bold text-rose-100 uppercase tracking-wide">Ward Threat Intelligence</h3>
                    </div>
                    
                    <div className="space-y-4">
                        {/* Dynamic Communication Alert */}
                        {commsRisk && (
                            <div className="bg-rose-900/20 border-l-4 border-rose-500 p-3 rounded">
                                <div className="flex items-center gap-2 mb-1">
                                    <Megaphone size={14} className="text-rose-400" />
                                    <span className="text-xs font-bold text-rose-200">COMMUNICATION DIRECTIVE</span>
                                </div>
                                <p className="text-sm text-white italic">"{commsRisk.recommendation}"</p>
                            </div>
                        )}

                        {/* Standard Risk List */}
                        {riskData?.risks_identified?.length > 0 ? (
                            riskData.risks_identified.filter(r => r !== commsRisk).map((risk, i) => (
                                <div key={i} className="flex gap-3">
                                    <div className="mt-1 flex-shrink-0">
                                        <AlertTriangle size={16} className="text-rose-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-rose-200">{risk.risk}</div>
                                        <p className="text-xs text-slate-400 mb-2 mt-1 leading-relaxed">{risk.details}</p>
                                        <div className="flex items-start gap-2 bg-rose-500/10 p-2 rounded border border-rose-500/20">
                                            <Lightbulb size={14} className="text-rose-400 mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-rose-100 font-medium">
                                                {risk.recommendation}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                                <CheckCircle2 size={32} className="mb-2 opacity-50 text-emerald-500" />
                                <span className="text-xs">No critical risks identified for this ward.</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* AUTHENTICITY GAPS */}
                <div className="bg-amber-950/10 border border-amber-900/30 p-5 rounded-xl h-full backdrop-blur-sm">
                    <div className="flex items-center space-x-2 mb-4 border-b border-amber-900/30 pb-3">
                        <ShieldAlert size={18} className="text-amber-500" />
                        <h3 className="text-sm font-bold text-amber-100 uppercase tracking-wide">Messaging Friction Points</h3>
                    </div>
                    <div className="space-y-3">
                        {authenticityGaps.slice(0, 3).map((gap, i) => (
                            <div key={i} className="bg-amber-900/10 p-3 rounded border border-amber-900/20">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="text-xs font-bold text-amber-200 bg-amber-900/40 px-2 py-1 rounded">
                                        Mismatch: {gap.target_archetype}
                                    </div>
                                    <div className="text-[10px] text-amber-500/70 uppercase font-bold">{gap.gap_type}</div>
                                </div>
                                <div className="text-xs text-slate-300 mb-2 leading-relaxed">
                                    Candidate's <span className="text-white font-semibold">{gap.candidate_driver}</span> driver conflicts with this group's moral foundation.
                                </div>
                                <div className="text-[10px] text-slate-400 italic border-l-2 border-amber-500/50 pl-2">
                                    "{gap.recommended_pivot}"
                                </div>
                            </div>
                        ))}
                        {authenticityGaps.length === 0 && (
                            <div className="text-xs text-slate-500 italic p-4 text-center">
                                Candidate messaging aligns well with typical ward archetypes.
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};